import { Divider, Stack, styled, SxProps, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { HTMLAttributes, ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  EventType,
  FieldPath,
  FieldValues,
  FormProvider,
  FormState,
  useForm,
  UseFormProps,
} from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  AutocompleteValue,
  type FormAutocompleteProps,
  type FormDateRangeProps,
  type FormEditableTextProps,
  type FormPhoneInputProps,
  type FormRichTextBubbleProps,
  type FormRichTextProps,
  FormSwitchProps,
  type FormTextFieldProps,
  type FormToggleGroupProps,
} from 'src/components/Forms';
import { FormDatePickerProps } from 'src/components/Forms/FormDatePicker/form-date-picker';
import { getFormField } from './get-form-field';

export interface CommonFormFieldProps<T extends string, TDefaultValue> {
  type: T;
  name: string;
  'data-testid'?: string;
  hidden?: boolean;
  defaultValue?: TDefaultValue;
  required?: boolean | string;
  prepopulate?: boolean;
  dependsOn?: string[];
  showsIf?: {
    name: string;
    value: AutocompleteValue | { id: (number | string)[]; label: string };
  }[];
  icon?: string | ReactNode;
  renderField?: (field: FormField, disabled?: boolean) => ReactNode;
  group?: FieldGroup & {
    renderGroup?: (group: FieldGroup) => ReactNode;
  };
}

export type FormField =
  | FormAutocompleteProps
  | FormTextFieldProps
  | FormRichTextProps
  | FormRichTextBubbleProps
  | FormSwitchProps
  | FormDatePickerProps
  | FormEditableTextProps
  | FormPhoneInputProps
  | FormDateRangeProps
  | FormToggleGroupProps;

export interface FormInfo<T> {
  defaultValues?: T;
  formState?: FormState<Record<string, string>>;
  info?: {
    name?: FieldPath<Record<string, string>>;
    type?: EventType;
    values?: unknown;
  };
}

interface FieldGroup {
  title?: string;
  icon?: string;
  sx?: SxProps<Theme>;
}

export type FormContainerProps<T> = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  fields: FormField[];
  value?: T;
  onSubmit: (values: T, formInfo?: FormInfo<T>) => void;
  handleBlur?: (values: T, formInfo?: FormInfo<T>) => void;
  options?: UseFormProps;
  watchable?: boolean;
  watchableSubmitOnlyChangedField?: boolean;
  disabled?: boolean;
  setFormMethods?: (methods: ReturnType<typeof useForm>) => void;
  onFieldChange?: (
    filedName: string | undefined,
    value: T,
    methods: ReturnType<typeof useForm>,
  ) => void;
  variant?: 'normal' | 'divided';
};

const FieldDivider = styled(Divider)(() => ({
  alignSelf: 'stretch',
}));

const DynamicFormContainer = styled('form', {
  name: 'DynamicFormContainer',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-end',
  width: '100%',
  gap: theme.spacing(0.5),

  '& > *': {
    flex: 1,
  },

  '& .Mui-error .MuiInput-input:not(:placeholder-shown)': {
    color: theme.palette.error.main,
  },
}));

function mapFieldsToDefaultValues<T>(fields: FormField[]) {
  return fields.reduce((acc, field) => {
    return {
      ...acc,
      [field.name]: 'defaultValue' in field ? field.defaultValue : null,
    };
  }, {} as T);
}

export const DynamicForm = <T extends FieldValues>(props: FormContainerProps<T>) => {
  const {
    children,
    options,
    fields,
    value,
    onSubmit,
    handleBlur,
    // If form is watchable, every change triggers submit callback.
    watchable = false,
    // If form is watchable and watchableSubmitOnlyChangedField, only changed field will be sent to callback
    watchableSubmitOnlyChangedField = false,
    variant = 'normal',
    disabled,
    onFieldChange,
    setFormMethods,
    ...rest
  } = props;

  const defaultValues = useMemo(
    () => value || mapFieldsToDefaultValues<T>(fields),
    [value, fields],
  );

  const methods = useForm({
    shouldFocusError: true,
    // Watchable form should be controlled, since all changes are immediately propagated to the parent component.
    // And if the source data is changed outside, these changes should also be reflected in the form (see ConversationDetails).
    // Reflecting outside changes using defaultValues is not possible and requires `reset()` call, which in turn triggers rerun
    // of `methods.watch()` callback, triggering unnecessary submit.
    // Using `values` for watchable controlled form eliminates this problem (but potentially may need adjustments in future).
    ...(watchable ? { values: defaultValues } : { defaultValues }),
    ...options,
  });
  const { setValue } = methods;

  useEffect(() => {
    // The current implementation makes it impossible to access form methods outside of the component. Which is higly needed in some cases.
    // It needs to be refactored so the form can accepts form methods as a prop so it can be controlled from the parent component.
    setFormMethods?.(methods);
  }, [setFormMethods, methods]);

  useEffect(() => {
    let subscription: ReturnType<typeof methods.watch> | undefined;

    if (onFieldChange) {
      subscription = methods.watch((value, { name }) =>
        onFieldChange(name, value as T, methods),
      );
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [methods, onFieldChange]);

  // Propagate form state changes if form is `watchable`
  useEffect(() => {
    if (!watchable) return;

    const sub = methods.watch((values, info) => {
      if (!watchableSubmitOnlyChangedField) {
        onSubmit(values as T, {
          info,
          formState: methods.formState,
          defaultValues,
        });
      } else if (info.name) {
        const changedField = info.name;
        const value = values[changedField] as T[keyof T];
        const changes = { [changedField as keyof T]: value } as T;
        onSubmit(changes, {
          info,
          formState: methods.formState,
          defaultValues,
        });
      }
    });
    return () => sub.unsubscribe();
  }, [methods, onSubmit, watchable, defaultValues, watchableSubmitOnlyChangedField]);

  useEffect(() => {
    fields.forEach((field) => {
      if (field.type === 'autocomplete' && field.prepopulate) {
        setValue(field.name, field.options[0]);
      }
      if (field.value) {
        setValue(field.name, field.value);
      }
    });
  }, [fields, setValue]);

  const handleSubmit = useCallback(
    (data: T) => {
      onSubmit(data, {
        formState: methods.formState,
        defaultValues,
      });
    },
    [defaultValues, methods.formState, onSubmit],
  );

  return (
    <FormProvider {...methods}>
      <DynamicFormContainer
        // TODO: Figure out a proper way to provide generic type for form values.
        // It should be specified as a generic parameter in useForm<T> call, but using <T> doesn't derive proper type for default values.
        // TODO: Investigate typings
        // @ts-expect-error Incorrect useForm typings.
        onSubmit={methods.handleSubmit(handleSubmit)}
        onBlur={() => {
          // micro delay to let values update by the time we trigger the blur callback
          setTimeout(() =>
            handleBlur?.(methods.getValues() as T, {
              formState: methods.formState,
              defaultValues,
            }),
          );
        }}
        noValidate
        {...rest}
      >
        {fields
          .map((field) => {
            const fieldComponent = mapFormFieldsToComponents(field, disabled);

            if (field.group?.title) {
              const groupComponent =
                field.group.renderGroup?.(field.group) ?? renderDefaultGroup(field.group);
              return [groupComponent, fieldComponent];
            }

            if (variant === 'divided' && !field.hidden) {
              return [fieldComponent, <FieldDivider key={`divider-${field.name}`} />];
            }

            return fieldComponent;
          })
          .flat()}
        {children}
      </DynamicFormContainer>
    </FormProvider>
  );
};

function renderDefaultGroup(group: FieldGroup) {
  return (
    <Stack direction="row" gap={1} alignItems="center" key={group.title! + group.icon!}>
      {!!group.icon && <DynamicIcon name={group.icon} fontSize="1.4rem" />}
      <Typography variant="subtitle2" sx={group.sx}>
        {group.title}
      </Typography>
    </Stack>
  );
}

function mapFormFieldsToComponents(field: FormField, disabled?: boolean) {
  if (field.hidden) return null;

  if (field.renderField) {
    return field.renderField(field, disabled);
  }

  return getFormField(field, disabled);
}
